import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { TrendingUp, X } from 'lucide-react';
import PeFactsAcademy from './components/PeFactsAcademy';
import PeFactsGenerator from './components/PeFactsGenerator';
import { PeFactsModalProps } from './types';

const PeFactsModal: React.FC<PeFactsModalProps> = ({ isOpen, onClose, showNotification }) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483000] flex items-center justify-center p-2 md:p-4 bg-slate-950/80 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="PEFacts Preisanpassungs-Texter"
      onClick={onClose}
    >
      <div
        className="bg-slate-900/95 rounded-[2rem] border border-slate-800 shadow-2xl overflow-hidden relative w-full max-w-7xl max-h-[95vh] flex flex-col text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0a0f1c] border-b border-slate-800 p-6 md:p-8 relative overflow-hidden shrink-0 z-20">
          <div className="absolute top-0 right-0 w-80 h-80 bg-[#E6C299]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#E6C299]/10 rounded-2xl flex items-center justify-center border border-[#E6C299]/30 shadow-[0_0_20px_rgba(230,194,153,0.15)] shrink-0">
                <TrendingUp className="w-7 h-7 text-[#E6C299]" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                    <span className="italic text-[#E6C299]">PE</span>
                    <span className="text-white">Facts</span>
                    <span className="text-slate-400 font-bold text-lg md:text-xl ml-2">
                      · Preisanpassungs-Texter
                    </span>
                  </h1>
                  <span className="px-2 py-0.5 bg-[#E6C299]/20 text-[#E6C299] text-[9px] font-bold uppercase tracking-widest border border-[#E6C299]/30 rounded">
                    Premium
                  </span>
                </div>
                <p className="text-xs md:text-sm text-slate-400 font-mono uppercase tracking-widest">
                  Die eiserne Preis-Strategie (No-Sorry)
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex bg-slate-900/80 border border-slate-700 px-4 py-2 rounded-xl text-xs text-slate-400 items-center gap-3 backdrop-blur-sm max-w-sm shadow-inner">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                <span>KI zwingt Text zu: Knappheit, Fakten & Autorität.</span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-10 h-10 rounded-xl border border-slate-700 bg-slate-900 hover:border-[#E6C299]/50 text-slate-400 hover:text-[#E6C299] flex items-center justify-center transition-colors"
                aria-label="Schließen"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 flex-1 min-h-0 relative z-10 overflow-y-auto lg:overflow-hidden">
          <div className="lg:col-span-6 min-h-0 lg:overflow-y-auto lg:overscroll-contain">
            <PeFactsAcademy />
          </div>
          <div className="lg:col-span-6 min-h-0 lg:overflow-y-auto lg:overscroll-contain">
            <PeFactsGenerator showNotification={showNotification} />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PeFactsModal;
